import { CheckCircle, Circle } from "@phosphor-icons/react";

/**
 * 4-step readiness strip for the admin Enrollment Detail page.
 *   Enrollment · Documents · Conversion · Billing-ready
 *
 * Each chip renders "done" or "pending" — used only to show operational
 * state at a glance, not as a gate.
 */
export default function ReadinessStrip({
  enrolled,
  converted,
  docsReady,
  billingProfileReady,
  docsSummary,   // { approvedCount, totalRequired, missingRequired, rejectedCount, uploadedCount }
  testId = "enr-readiness",
}) {
  const items = [
    {
      key: "enrollment",
      label: "Enrollment",
      done: enrolled,
      hint: enrolled ? "Stage: enrolled" : "Move stage to enrolled",
    },
    {
      key: "documents",
      label: "Documents",
      done: docsReady,
      hint: docsSummary
        ? `${docsSummary.approvedCount + docsSummary.uploadedCount}/${docsSummary.totalRequired} required`
        : undefined,
    },
    {
      key: "conversion",
      label: "Converted",
      done: converted,
      hint: converted ? "Family & child linked" : "Run Convert to Active Family",
    },
    {
      key: "billing",
      label: "Billing ready",
      done: billingProfileReady && enrolled && converted && docsReady,
      hint: !billingProfileReady
        ? "No billing profile yet"
        : !enrolled
          ? "Stage not yet enrolled"
          : !converted
            ? "Family not yet converted"
            : !docsReady
              ? "Required documents incomplete"
              : "All checks passed",
    },
  ];

  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        Operational readiness
      </p>
      <ol className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {items.map((it) => {
          const Icon = it.done ? CheckCircle : Circle;
          return (
            <li
              key={it.key}
              data-testid={`${testId}-${it.key}`}
              data-state={it.done ? "done" : "pending"}
              className={`flex items-start gap-2.5 rounded-2xl border p-3 ${
                it.done
                  ? "border-primary/30 bg-primary/5"
                  : "border-neutral-200 bg-neutral-50"
              }`}
            >
              <Icon
                size={18}
                weight={it.done ? "fill" : "duotone"}
                className={it.done ? "mt-0.5 text-primary" : "mt-0.5 text-neutral-400"}
              />
              <div className="min-w-0">
                <p className="font-heading text-sm font-semibold tracking-tight text-neutral-900">
                  {it.label}
                </p>
                {it.hint && (
                  <p className="mt-0.5 text-[11px] text-neutral-500">{it.hint}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
